import React, { useState, useEffect } from "react";
import { Play, Square, Radio } from "lucide-react";
import { motion } from "motion/react";
import { useLocation } from "react-router-dom";
import { LoadingSpinner } from "./LoadingSpinner";

interface TacitonicRadioProps {
  variant?: "static" | "floating";
}

export function TacitonicRadio({ variant = "static" }: TacitonicRadioProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handlePlay = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setIsPlaying(true);
    setIsIframeLoaded(false);
  };

  const handleStop = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setIsPlaying(false);
    setIsIframeLoaded(false);
  };

  const toggleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const playlistEmbedUrl = "https://open.spotify.com/embed/playlist/6Dhj9838l50lWCgNXR9rip?utm_source=generator&theme=0&autoplay=1";

  if (variant === "floating") {
    return (
      <div 
        className="fixed top-[104px] right-[14px] md:top-[114px] md:right-[18px] lg:top-[142px] lg:right-[30px] z-[100] flex flex-col items-end pointer-events-none"
        onMouseLeave={() => setIsOpen(false)}
      >
        <motion.button
          aria-label="Toggle tacitonic radio"
          className={`w-10 h-10 md:w-[52px] md:h-[52px] lg:w-14 lg:h-14 bg-black border-2 border-white flex items-center justify-center hover:scale-110 transition-transform shadow-xl cursor-none pointer-events-auto ${isPlaying ? 'text-[#9af104]' : 'text-white'}`}
          onClick={toggleOpen}
          onMouseEnter={() => !isMobile && setIsOpen(true)}
        >
          <Radio size={isMobile ? 18 : 24} className={isPlaying ? "animate-pulse" : ""} />
        </motion.button>

        {/* Expanded Player - Always in DOM to keep playback going */}
        <motion.div
          initial={false}
          animate={{ 
            opacity: isOpen ? 1 : 0,
            scale: isOpen ? 1 : 0.9,
            y: isOpen ? 0 : -20,
            pointerEvents: isOpen ? "auto" : "none"
          }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="pt-6 origin-top-right pointer-events-auto"
          onMouseEnter={() => setIsOpen(true)}
        >
          <div className="w-[320px] md:w-[600px] bg-black border-2 border-white p-4 md:p-8 shadow-2xl">
            <div className={`flex flex-col md:flex-row justify-between items-start md:items-end ${isPlaying ? 'mb-8' : 'mb-0'} border-b-[3px] border-white pb-4 gap-4 transition-all duration-500`}>
        <div>
          <h3 className="text-[18px] md:text-[22px] font-bold uppercase tracking-tighter font-display">tacitonic Radio</h3>
        </div>
              <div className="flex gap-4">
                {!isPlaying && (
                  <button 
                    onClick={handlePlay}
                    className="group/btn flex items-center gap-2 border-2 border-white/20 pl-4 pr-3 py-2 hover:bg-white hover:text-black transition-all duration-300 font-mono text-[10px] uppercase tracking-[0.2em] font-bold w-fit"
                  >
                    Play 
                    <Play size={14} className="text-[#9af104] opacity-100 transition-transform group-hover/btn:scale-110" />
                  </button>
                )}
                {isPlaying && (
                  <button 
                    onClick={handleStop}
                    className="group/btn flex items-center gap-2 border-2 border-white/20 pl-4 pr-3 py-2 hover:bg-white hover:text-black transition-all duration-300 font-mono text-[10px] uppercase tracking-[0.2em] font-bold w-fit"
                  >
                    Stop <Square size={14} className="text-red-500 opacity-100 transition-transform group-hover/btn:scale-110" />
                  </button>
                )}
              </div>
            </div>
            
            <div 
              className="relative w-full overflow-hidden bg-transparent transition-all duration-500 ease-in-out" 
              style={{ 
                height: isPlaying ? "152px" : "0px",
                opacity: isPlaying ? 1 : 0
              }}
            >
              {!isIframeLoaded && isPlaying && <LoadingSpinner isDark={true} />}
              {isPlaying && (
                <iframe 
                  style={{ borderRadius: "0px" }} 
                  src={playlistEmbedUrl} 
                  width="100%" 
                  height="152" 
                  frameBorder="0" 
                  allowFullScreen 
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
                  loading="lazy"
                  onLoad={() => setIsIframeLoaded(true)}
                  className={`grayscale-[0.5] hover:grayscale-0 transition-all duration-700 cursor-none ${isIframeLoaded ? 'opacity-100' : 'opacity-0'}`}
                ></iframe>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="border-2 border-white/20 p-4 md:p-8 group hover:border-white transition-all duration-300">
      <div className={`flex flex-col md:flex-row justify-between items-start md:items-end ${isPlaying ? 'mb-8' : 'mb-0'} border-b-[3px] border-white pb-4 gap-4 transition-all duration-500`}>
        <div>
          <h3 className="text-[22px] md:text-[34px] font-bold uppercase tracking-tighter font-display">tacitonic Radio</h3>
        </div>
        <div className="flex flex-wrap gap-4">
          {!isPlaying && (
            <button 
              onClick={handlePlay}
              className="group/btn flex items-center gap-2 md:gap-3 border-2 border-white/20 pl-5 pr-4 py-3 md:pl-6 md:pr-4 md:py-3 hover:bg-white hover:text-black transition-all duration-300 font-mono text-[10px] md:text-[12px] uppercase tracking-[0.2em] font-bold w-fit"
            >
              Play
              <Play className="text-[#9af104] opacity-100 transition-transform group-hover/btn:scale-110 w-3.5 h-3.5 md:w-4 md:h-4" />
            </button>
          )}
          {isPlaying && (
            <button 
              onClick={handleStop}
              className="group/btn flex items-center gap-2 md:gap-3 border-2 border-white/20 pl-5 pr-4 py-3 md:pl-6 md:pr-4 md:py-3 hover:bg-white hover:text-black transition-all duration-300 font-mono text-[10px] md:text-[12px] uppercase tracking-[0.2em] font-bold w-fit"
            >
              Stop <Square className="text-red-500 opacity-100 transition-transform group-hover/btn:scale-110 w-3.5 h-3.5 md:w-4 md:h-4" />
            </button>
          )}
        </div>
      </div>
      
      <div 
        className="relative w-full overflow-hidden bg-transparent transition-all duration-500 ease-in-out" 
        style={{ 
          height: isPlaying ? "152px" : "0px",
          opacity: isPlaying ? 1 : 0
        }}
      >
        {!isIframeLoaded && isPlaying && <LoadingSpinner isDark={true} />}
        {isPlaying && (
          <iframe 
            style={{ borderRadius: "0px" }} 
            src={playlistEmbedUrl} 
            width="100%" 
            height="152" 
            frameBorder="0" 
            allowFullScreen 
            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" 
            loading="lazy"
            onLoad={() => setIsIframeLoaded(true)}
            className={`grayscale-[0.5] hover:grayscale-0 transition-all duration-700 cursor-none ${isIframeLoaded ? 'opacity-100' : 'opacity-0'}`}
          ></iframe>
        )}
      </div>
    </div>
  );
}
